import React from "react";
import Flexbox from "components/UI/FlexboxContainer";
import { renderInput } from "components/UI/RenderFields";
import { BUSINESS_UNIT_FORM_FIELDS, ORGANIZATION_CONSTANTS, UNIT_FORM_RULES } from "constants/organization";
import { Control, Controller, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { Main } from "styles/themeColors";
import SelectComponent from "components/UI/Select";
import Brand from "data/Brand";
import InputComponent from "components/UI/Input";
import styled from "styled-components";
import { Size } from "lib/styles";
import { useReadPermission } from "hooks/useUserRoles";
import { PermissionCode } from "data/UserPermissions";
import Checkbox from "components/UI/Checkbox";
import { useAuth } from "lib/Auth";
import { hasOfmpAdminProfile } from "data/User";

const Logo = styled.img`
  flex: 1;
  max-height: 150px;
  max-width: 150px;
  object-fit: contain;
  object-position: center;
`;

const LogoContainer = styled.div`
  align-items: start;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${Size.medium};
  margin-bottom: ${Size.medium};
`;

const BrandHeader = styled.h6`
    font-size: 16px;
    font-weight: 500;
    margin-bottom: ${Size.medium};
    margin-top: ${Size.medium};
    `;

const BrandNote = styled.p`
    font-size: 12px;
    color: ${Main.disabled};
    margin-top: ${Size.small};
    `;

interface Props {
    control: Control<any>;
    errors?: any;
    setValue?: UseFormSetValue<any>;
    watch?: any;
    getValues?: UseFormGetValues<any>;
    clearErrors?: any;
    setError?: any;
    brands: Brand[];
    width?: string;
    disabled?: boolean;
}

const AboutBusinessUnit = (props: Props) => {
    const { control, errors, watch, brands,width,disabled } = props;
    const { user } = useAuth();
    const isOfmpAdmin = hasOfmpAdminProfile(user!);

    const brandId = watch(BUSINESS_UNIT_FORM_FIELDS.BRAND);
    const brand = brands.find((brand) => {
        return brand.id === brandId
    })
    const codeInput =
        (
            <InputComponent
                id='brand-code'
                disabled={true}
                labelName={ORGANIZATION_CONSTANTS.BRAND_CODE}
                value={brand?.code || ''}
                placeholder={''}
                displayAsterisk={false}
                isOptional={true}
                showOptional={false}
            />
        );

    const brandLogo =
        brand && brand.media && brand.media.length > 0
            ? <Logo src={process.env.REACT_APP_MEDIA_URL + brand.media[0].path} />
            : null

    const logoContainer =
        brandLogo ? <LogoContainer>
            {brandLogo}
        </LogoContainer> : null

    const {readOnly} = useReadPermission(PermissionCode.EDIT_REMOVE_BUSINESS_UNIT);
    const nameController = <Controller
        name={BUSINESS_UNIT_FORM_FIELDS.NAME}
        control={control}
        rules={UNIT_FORM_RULES.UNIT_NAME}
        render={({ field }) => (
            renderInput(field, BUSINESS_UNIT_FORM_FIELDS.NAME, ORGANIZATION_CONSTANTS.BUSINESS_UNIT_NAME, ORGANIZATION_CONSTANTS.ENTER_UNIT_NAME, errors.name?.message, !!errors.name, false, '',readOnly)
        )}
        disabled={disabled}
    />

    const brandController = <Controller
        name={BUSINESS_UNIT_FORM_FIELDS.BRAND}
        control={control}
        rules={UNIT_FORM_RULES.BRAND}
        disabled={disabled}
        render={({ field }) => (
            <Flexbox flexDirection="column" width='100%'>
                <SelectComponent
                    {...field}
                    label={ORGANIZATION_CONSTANTS.ADD_BRAND}
                    labelPosition="top"
                    options={brands.map((brand) => ({
                        value: `${brand.name}`,
                        id: brand.id?.toString(),
                        disabled: brand.isAssigned === true,
                    }))}
                    onChange={(event: any) => {
                        field.onChange(Number(event.target.id));
                    }}
                    value={brands?.find((brand) => brand.id === field.value)?.name || ''}
                    defaultValue={ORGANIZATION_CONSTANTS.SELECT_BRAND}
                    disabledColor={Main.disabled}
                    isInvalid={!!errors?.brandId?.message}
                    errorMessage={errors?.brandId?.message}
                    isOptional={false}
                    displayAsterisk={!disabled}
                    errroLabelPostion="bottom"
                    isRedLable={errors?.brandId?.message ? true : false}
                    disabled={disabled}
                    readOnly={readOnly}
                />
                {!readOnly && !disabled && (
                    <BrandNote>{ORGANIZATION_CONSTANTS.BRAND_PRODUCTS_AUTO_ATTACH_NOTE}</BrandNote>
                )}
            </Flexbox>
        )}
    />

    const subscriptionController = isOfmpAdmin ? (
        <Controller
            name={BUSINESS_UNIT_FORM_FIELDS.ENABLE_SUBSCRIPTION}
            control={control}
            render={({ field }) => (
                <Checkbox
                    id="enable-subscription"
                    checked={!!field.value}
                    onChange={(checked) => field.onChange(checked)}
                    label={ORGANIZATION_CONSTANTS.ENABLE_SUBSCRIPTION}
                    disabled={disabled || readOnly}
                    permissionCode={PermissionCode.EDIT_REMOVE_BUSINESS_UNIT}
                />
            )}
        />
    ) : null;

    return (
        <Flexbox flexDirection='column' width={width ? width : '40%'} gap={'10px'}>
            {nameController}
            <BrandHeader>{ORGANIZATION_CONSTANTS.BRAND}</BrandHeader>
            {codeInput}
            {logoContainer}
            {brandController}
            {subscriptionController}
        </Flexbox>
    );
}

export default AboutBusinessUnit;